import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { moveActor } from '../../store/sceneSlice';
import { getHandLandmarkIndex, normalizeFinger, DEFAULT_FINGER } from '../../utils/handLandmarks';
import { NUMBER_DETECT_CROP } from '../../constants/numberDetection';
import '../../css/HumanDetectionFullStage.css';

const normalizeFacePartKey = (value = 'nose') => {
    const map = {
        nose: 'nose',
        mouth: 'mouth',
        leftEye: 'leftEye',
        'left eye': 'leftEye',
        lefteye: 'leftEye',
        rightEye: 'rightEye',
        'right eye': 'rightEye',
        righteye: 'rightEye',
        betweenEyes: 'betweenEyes',
        'between eyes': 'betweenEyes',
        betweeneyes: 'betweenEyes',
        leftEar: 'leftEar',
        'left ear': 'leftEar',
        leftear: 'leftEar',
        rightEar: 'rightEar',
        'right ear': 'rightEar',
        rightear: 'rightEar',
        topOfHead: 'topOfHead',
        'top of head': 'topOfHead',
        topofhead: 'topOfHead',
    };
    if (typeof value !== 'string') return 'nose';
    const trimmed = value.trim();
    if (map[trimmed]) return map[trimmed];
    const normalized = trimmed.toLowerCase().replace(/\s+/g, '');
    return map[normalized] || trimmed;
};

const formatLabel = (value = '') => {
    if (!value) return '';
    return value.replace(/([A-Z])/g, ' $1').replace(/\s+/g, ' ').trim().toLowerCase();
};

// Throttle time in milliseconds to prevent sprite from moving too fast
const MOVE_COOLDOWN = 200;
// Run ML at ~12-15fps instead of every rAF to ease CPU/GPU load
const FRAME_INTERVAL_MS = 80;
const COLOR_SAMPLE_WIDTH = 160;
const COLOR_SAMPLE_HEIGHT = 90;
const COLOR_SAMPLE_STEP = 8;
const COLOR_ROI_FACE_PAD = 0.25;
const COLOR_ROI_HAND_SIZE = 120;
const COLOR_ROI_HAND_MIN_SCORE = 0.3;
const COLOR_ROI_MIN_SIZE = 40;
let lastMoveTime = 0;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const normalizeRoi = (roi, videoWidth, videoHeight) => {
    if (!roi || !videoWidth || !videoHeight) return null;
    const x = clamp(roi.x ?? 0, 0, Math.max(0, videoWidth - 1));
    const y = clamp(roi.y ?? 0, 0, Math.max(0, videoHeight - 1));
    const maxWidth = Math.max(0, videoWidth - x);
    const maxHeight = Math.max(0, videoHeight - y);
    const width = clamp(roi.width ?? maxWidth, 0, maxWidth);
    const height = clamp(roi.height ?? maxHeight, 0, maxHeight);
    if (width < COLOR_ROI_MIN_SIZE || height < COLOR_ROI_MIN_SIZE) return null;
    return {
        x,
        y,
        width,
        height,
        source: roi.source || 'custom'
    };
};

const buildColorSamplingRoi = ({
    faceBoxes = [],
    leftHand,
    rightHand,
    videoWidth,
    videoHeight
}) => {
    if (!videoWidth || !videoHeight) return null;

    if (Array.isArray(faceBoxes) && faceBoxes.length > 0) {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        faceBoxes.forEach((box) => {
            if (!box) return;
            const x1 = box.x ?? 0;
            const y1 = box.y ?? 0;
            const x2 = x1 + (box.width ?? 0);
            const y2 = y1 + (box.height ?? 0);
            minX = Math.min(minX, x1);
            minY = Math.min(minY, y1);
            maxX = Math.max(maxX, x2);
            maxY = Math.max(maxY, y2);
        });

        if (Number.isFinite(minX) && Number.isFinite(minY) && Number.isFinite(maxX) && Number.isFinite(maxY)) {
            const width = Math.max(1, maxX - minX);
            const height = Math.max(1, maxY - minY);
            const padX = width * COLOR_ROI_FACE_PAD;
            const padY = height * COLOR_ROI_FACE_PAD;
            return normalizeRoi({
                x: minX - padX,
                y: minY - padY,
                width: width + padX * 2,
                height: height + padY * 2,
                source: 'face'
            }, videoWidth, videoHeight);
        }
    }

    const handPoints = [];
    if (leftHand?.score >= COLOR_ROI_HAND_MIN_SCORE && leftHand?.position) {
        handPoints.push(leftHand.position);
    }
    if (rightHand?.score >= COLOR_ROI_HAND_MIN_SCORE && rightHand?.position) {
        handPoints.push(rightHand.position);
    }

    if (handPoints.length > 0) {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        handPoints.forEach((pt) => {
            if (!pt) return;
            const x1 = (pt.x ?? 0) - COLOR_ROI_HAND_SIZE / 2;
            const y1 = (pt.y ?? 0) - COLOR_ROI_HAND_SIZE / 2;
            const x2 = (pt.x ?? 0) + COLOR_ROI_HAND_SIZE / 2;
            const y2 = (pt.y ?? 0) + COLOR_ROI_HAND_SIZE / 2;
            minX = Math.min(minX, x1);
            minY = Math.min(minY, y1);
            maxX = Math.max(maxX, x2);
            maxY = Math.max(maxY, y2);
        });

        return normalizeRoi({
            x: minX,
            y: minY,
            width: Math.max(1, maxX - minX),
            height: Math.max(1, maxY - minY),
            source: 'hand'
        }, videoWidth, videoHeight);
    }

    return null;
};

const HumanDetectionFullStage = ({ selectedActorId }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const dispatch = useDispatch();
    const animationIdRef = useRef(null);
    const streamRef = useRef(null);
    const detectionActive = useRef(false);
    const modelsInitialized = useRef(false);
    const detectorRef = useRef(null);
    const tfRef = useRef(null);
    const posenetRef = useRef(null);
    const faceApiRef = useRef(null);
    const handModuleRef = useRef(null);
    const faceLandmarkerRef = useRef(null);
    const faceMeshReadyRef = useRef(false);
    const lastDetectionTimeRef = useRef(0);
    const colorCanvasRef = useRef(null);
    const colorCtxRef = useRef(null);

    const videoOpacity = useSelector((state) => state.scene.videoOpacity);
    const numberDetectEnabled = useSelector((state) => state.scene.numberDetectEnabled);
    const { scenes, currentSceneIndex, simulatorRobots, selectedSimRobotId } = useSelector((s) => s.scene);
    const actors = scenes?.[currentSceneIndex]?.actors ?? [];
    const selectedSimRobot = simulatorRobots?.find(r => r.id === selectedSimRobotId) || null;
    const faceSelectionRef = useRef(null);
    const handSelectionRef = useRef(null);
    const emotionHudEnabledRef = useRef(false);

    const selectedActor = useMemo(() => {
        if (selectedSimRobot) return selectedSimRobot;
        if (selectedActorId) {
            const stageActor = actors.find(actor => actor.id === selectedActorId);
            if (stageActor) return stageActor;
        }
        return actors[0] || null;
    }, [actors, selectedSimRobot, selectedActorId]);

    const facePartSelection = useMemo(() => {
        const targetActors = selectedActor ? [selectedActor] : actors;
        for (const actor of targetActors) {
            const blocks = actor?.scripts;
            if (!Array.isArray(blocks)) continue;
            const block = blocks.find(b => b?.type === 'facePartAction' || b?.name === 'Go To Face Part');
            if (block) {
                return {
                    actorId: actor.id,
                    part: typeof block.part === 'string' ? block.part : 'nose'
                };
            }
        }
        return null;
    }, [actors, selectedActor]);

    const handPartSelection = useMemo(() => {
        const targetActors = selectedActor ? [selectedActor] : actors;
        for (const actor of targetActors) {
            const blocks = actor?.scripts;
            if (!Array.isArray(blocks)) continue;
            const block = blocks.find(b => b?.type === 'handPartAction' || b?.name === 'Go To Hand Part');
            if (block) {
                return {
                    actorId: actor.id,
                    finger: typeof block.finger === 'string' ? block.finger : DEFAULT_FINGER,
                };
            }
        }
        return null;
    }, [actors, selectedActor]);

    const hasEmotionBlock = useMemo(() => {
        const hasEmotionInScripts = (scripts = []) => Array.isArray(scripts) && scripts.some(block =>
            block?.type === 'emotionDetected' || block?.name === 'Emotion Detected'
        );

        const actorHasBlock = Array.isArray(actors) && actors.some(actor => hasEmotionInScripts(actor?.scripts));
        const robotHasBlock = Array.isArray(simulatorRobots) && simulatorRobots.some(robot => hasEmotionInScripts(robot?.scripts));

        return actorHasBlock || robotHasBlock;
    }, [actors, simulatorRobots]);

    useEffect(() => {
        faceSelectionRef.current = facePartSelection;
    }, [facePartSelection]);

    useEffect(() => {
        handSelectionRef.current = handPartSelection;
    }, [handPartSelection]);

    useEffect(() => {
        emotionHudEnabledRef.current = hasEmotionBlock;
    }, [hasEmotionBlock]);

    const getFirstActor = useCallback(() => {
        return actors.find(actor => actor.visible) || actors[0];
    }, [actors]);

    const [net, setNet] = useState(null);
    const [faceNet, setFaceNet] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [cameraStarted, setCameraStarted] = useState(false);
    const [peopleCount, setPeopleCount] = useState(0);
    const [handCount, setHandCount] = useState(0);
    const [faceCount, setFaceCount] = useState(0);
    const [stageRect, setStageRect] = useState({ top: 110, left: 380, width: 830, height: 460 });
    const [loadingStatus, setLoadingStatus] = useState('Initializing...');
    const [showAiLoadingBanner, setShowAiLoadingBanner] = useState(false);
    const [detectionStatus, setDetectionStatus] = useState('Not started');

    const handleSpriteMovement = useCallback((pointingData) => {
        console.log('🎯 Movement handler called with:', pointingData);

        if (!pointingData) return;

        const currentTime = Date.now();
        if (currentTime - lastMoveTime < MOVE_COOLDOWN) {
            console.log('⏰ Still in cooldown, skipping movement');
            return;
        }

        console.log('🎭 Available actors:', actors.length);
        const actor = actors.find(actor => actor.visible) || actors[0];
        console.log('🎯 Selected actor:', actor?.id);

        if (!actor) {
            console.log('❌ No actor available for movement');
            return;
        }

        const { direction, confidence } = pointingData;

        let moveX = 0;
        let moveY = 0;

        switch (direction) {
            case 'up':
                moveY = -1;
                break;
            case 'down':
                moveY = 1;
                break;
            case 'left':
                moveX = -1;
                break;
            case 'right':
                moveX = 1;
                break;
            default:
                console.log('❌ Invalid direction:', direction);
                return;
        }

        try {
            console.log('🚀 DISPATCHING MOVEMENT:', { actorId: actor.id, dx: moveX, dy: moveY });

            dispatch(moveActor({
                actorId: actor.id,
                dx: moveX,
                dy: moveY,
                fromScript: true
            }));

            lastMoveTime = currentTime;
            console.log(`✅ ✅ ✅ SPRITE MOVED ${direction.toUpperCase()} ✅ ✅ ✅`);

        } catch (error) {
            console.error('❌ Movement dispatch failed:', error);
        }
    }, [dispatch, actors]);

    const drawPointingIndicator = useCallback((ctx, pointingData) => {
        if (!pointingData) return;

        const { direction, confidence } = pointingData;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const arrowLength = 100 * confidence;

        let endX = centerX;
        let endY = centerY;

        switch (direction) {
            case 'up':
                endY = centerY - arrowLength;
                break;
            case 'down':
                endY = centerY + arrowLength;
                break;
            case 'left':
                endX = centerX - arrowLength;
                break;
            case 'right':
                endX = centerX + arrowLength;
                break;
        }

        ctx.strokeStyle = `rgba(255, 215, 0, ${confidence})`;
        ctx.lineWidth = 8;
        ctx.lineCap = 'round';

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        const angle = Math.atan2(endY - centerY, endX - centerX);
        const arrowHeadLength = 20;

        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(
            endX - arrowHeadLength * Math.cos(angle - Math.PI / 6),
            endY - arrowHeadLength * Math.sin(angle - Math.PI / 6)
        );
        ctx.moveTo(endX, endY);
        ctx.lineTo(
            endX - arrowHeadLength * Math.cos(angle + Math.PI / 6),
            endY - arrowHeadLength * Math.sin(angle + Math.PI / 6)
        );
        ctx.stroke();

        ctx.fillStyle = `rgba(255, 215, 0, ${confidence})`;
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`👉 ${direction.toUpperCase()}`, centerX, centerY - 150);
        ctx.fillText(`${(confidence * 100).toFixed(0)}%`, centerX, centerY - 120);
    }, []);

    const updateStageRect = useCallback(() => {
        const stageElement = document.querySelector('.stage-area-section') ||
            document.querySelector('[class*="stage"]') ||
            document.querySelector('.stage') ||
            document.querySelector('.middle-section .stage-area-section');

        if (stageElement) {
            const rect = stageElement.getBoundingClientRect();
            setStageRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height
            });
        }
    }, []);

    const ngOnInit = useCallback(async () => {
        if (modelsInitialized.current) return;

        setShowAiLoadingBanner(true);

        try {
            setLoadingStatus('Loading AI libraries...');
            console.log('📚 Loading AI libraries lazily...');

            const [tfModule, posenetModule, faceapiModule, handModule, visionTasks] = await Promise.all([
                import('@tensorflow/tfjs'),
                import('@tensorflow-models/posenet'),
                import('@vladmandic/face-api'),
                import('../../utils/HandDetection'),
                import('@mediapipe/tasks-vision')
            ]);

            tfRef.current = tfModule.default || tfModule;
            posenetRef.current = posenetModule;
            faceApiRef.current = faceapiModule;
            handModuleRef.current = handModule;

            try {
                await tfRef.current.setBackend('webgl');
                console.log('✅ Using WebGL backend');
            } catch (webglError) {
                console.warn('WebGL not available, using CPU backend:', webglError);
                await tfRef.current.setBackend('cpu');
            }

            await tfRef.current.ready();
            console.log('✅ TensorFlow ready with backend:', tfRef.current.getBackend());

            setLoadingStatus('Loading PoseNet model (ResNet50)...');
            console.log('🔄 Loading PoseNet ResNet50 model...');

            const poseModel = await posenetRef.current.load({
                architecture: 'ResNet50',
                outputStride: 32,
                inputResolution: { width: 257, height: 257 },
            });

            setNet(poseModel);
            console.log('✅ PoseNet model loaded successfully');

            try {
                setLoadingStatus('Loading Face Detection models (with age & gender)...');
                console.log('🔄 Loading Face Detection models with age & gender...');

                await faceApiRef.current.tf.setBackend(tfRef.current.getBackend());
                await faceApiRef.current.tf.ready();

                const MODEL_URL = 'https://vladmandic.github.io/face-api/model';

                await Promise.all([
                    faceApiRef.current.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
                    faceApiRef.current.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
                    faceApiRef.current.nets.faceExpressionNet.loadFromUri(MODEL_URL),
                    faceApiRef.current.nets.ageGenderNet.loadFromUri(MODEL_URL),
                ]);

                setFaceNet(true);
                console.log('✅ Face Detection models (with age & gender) loaded successfully');
            } catch (faceError) {
                console.warn('Face detection models failed to load:', faceError);
                setFaceNet(false);
            }

            console.log('🎥 Initializing MediaPipe Face Landmarker...');
            try {
                const { FilesetResolver, FaceLandmarker } = visionTasks;
                const filesetResolver = await FilesetResolver.forVisionTasks(
                    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.22-rc.20250304/wasm'
                );
                faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(filesetResolver, {
                    baseOptions: {
                        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
                    },
                    runningMode: 'VIDEO',
                    numFaces: 1,
                    outputFaceBlendshapes: false,
                    outputFacialTransformationMatrixes: true,
                });
                faceMeshReadyRef.current = true;
                console.log('? MediaPipe Face Landmarker ready');
            } catch (meshErr) {
                console.error('?? Face Landmarker failed to load:', meshErr);
                faceMeshReadyRef.current = false;
            }

            console.log('🎥 Initializing hand detection...');
            try {
                detectorRef.current = await handModuleRef.current.initHandDetection();
                console.log('✅ Hand detection initialized');
            } catch (err) {
                console.error('❌ Hand detection initialization failed:', err);
            }

            modelsInitialized.current = true;
            setLoadingStatus('All models loaded successfully');
            console.log('✅ All AI models initialized successfully');

        } catch (error) {
            console.error('❌ Model loading error:', error);
            setLoadingStatus(`Error: ${error.message}`);
            modelsInitialized.current = false;
        } finally {
            setShowAiLoadingBanner(false);
        }
    }, []);

    useEffect(() => {
        const initializeComponent = async () => {
            updateStageRect();
        };

        initializeComponent();
        window.addEventListener('resize', updateStageRect);

        return () => {
            window.removeEventListener('resize', updateStageRect);
            if (animationIdRef.current) {
                cancelAnimationFrame(animationIdRef.current);
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, [updateStageRect]);

    const startCamera = useCallback(async () => {
        if (cameraStarted) {
            console.log('⚠️ Camera already started');
            return;
        }

        if (!modelsInitialized.current) {
            console.log('⚠️ Models not initialized yet');
            await ngOnInit();
        }

        try {
            console.log('🎥 Starting camera...');
            setDetectionStatus('Starting camera...');
            setIsVisible(true);

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 640, max: 640 },
                    height: { ideal: 360, max: 360 },
                    frameRate: { ideal: 15, max: 20 },
                    facingMode: 'user'
                }
            });

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;

                return new Promise((resolve) => {
                    videoRef.current.onloadedmetadata = () => {
                        videoRef.current.play().then(() => {
                            setCameraStarted(true);
                            setDetectionStatus('Camera started, detection running');
                            console.log('✅ Camera started with detection');
                            resolve();
                        }).catch(error => {
                            console.error('Video play error:', error);
                            setDetectionStatus(`Error: ${error.message}`);
                        });
                    };
                });
            }
        } catch (error) {
            console.error('❌ Camera access error:', error);
            setDetectionStatus(`Camera error: ${error.message}`);
        }
    }, [cameraStarted, ngOnInit]);

    const stopCamera = useCallback(() => {
        console.log('🛑 Stopping camera and detection');
        setDetectionStatus('Detection stopped');

        detectionActive.current = false;

        if (animationIdRef.current) {
            cancelAnimationFrame(animationIdRef.current);
            animationIdRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        if (typeof window !== 'undefined' && window.humanDetectionData) {
            window.humanDetectionData.cameraActive = false;
            window.humanDetectionData.videoEl = null;
        }

        setIsVisible(false);
        setCameraStarted(false);
        setPeopleCount(0);
        setHandCount(0);
        setFaceCount(0);
    }, []);

    const checkHandDetected = useCallback((poses) => {
        if (!poses || poses.length === 0) return 0;

        const scoreThreshold = 0.2;
        let handsFound = 0;

        poses.forEach(pose => {
            if (pose.score > 0.2) {
                const leftWrist = pose.keypoints[9];
                const rightWrist = pose.keypoints[10];

                if (leftWrist && leftWrist.score > scoreThreshold) handsFound++;
                if (rightWrist && rightWrist.score > scoreThreshold) handsFound++;
            }
        });

        return Math.min(handsFound, 2);
    }, []);

    const checkFaceDetected = useCallback(async (videoElement) => {
        const faceapi = faceApiRef.current;
        if (!faceNet || !videoElement || !faceapi) return 0;

        try {
            const detections = await faceapi.detectAllFaces(
                videoElement,
                new faceapi.TinyFaceDetectorOptions({
                    inputSize: 416,
                    scoreThreshold: 0.4
                })
            ).withFaceLandmarks().withFaceExpressions().withAgeAndGender();

            const validDetections = detections.filter(detection => {
                const box = detection.detection.box;
                return box.width > 30 && box.height > 30;
            });

            return Math.min(validDetections.length, 3);
        } catch (error) {
            console.error('Face detection error:', error);
            return 0;
        }
    }, [faceNet]);

    const getColorSamples = useCallback((roi) => {
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) return null;
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;

        let canvas = colorCanvasRef.current;
        if (!canvas) {
            canvas = document.createElement('canvas');
            colorCanvasRef.current = canvas;
        }

        if (canvas.width !== COLOR_SAMPLE_WIDTH || canvas.height !== COLOR_SAMPLE_HEIGHT) {
            canvas.width = COLOR_SAMPLE_WIDTH;
            canvas.height = COLOR_SAMPLE_HEIGHT;
        }

        let ctx = colorCtxRef.current;
        if (!ctx) {
            ctx = canvas.getContext('2d', { willReadFrequently: true });
            colorCtxRef.current = ctx;
        }
        if (!ctx) return null;

        try {
            const validRoi = normalizeRoi(roi, videoWidth, videoHeight);
            const sourceX = validRoi?.x ?? 0;
            const sourceY = validRoi?.y ?? 0;
            const sourceW = validRoi?.width ?? videoWidth;
            const sourceH = validRoi?.height ?? videoHeight;

            ctx.drawImage(
                video,
                sourceX,
                sourceY,
                sourceW,
                sourceH,
                0,
                0,
                COLOR_SAMPLE_WIDTH,
                COLOR_SAMPLE_HEIGHT
            );
            const imageData = ctx.getImageData(0, 0, COLOR_SAMPLE_WIDTH, COLOR_SAMPLE_HEIGHT);
            const data = imageData.data;
            const colors = [];

            for (let y = 0; y < COLOR_SAMPLE_HEIGHT; y += COLOR_SAMPLE_STEP) {
                for (let x = 0; x < COLOR_SAMPLE_WIDTH; x += COLOR_SAMPLE_STEP) {
                    const idx = (y * COLOR_SAMPLE_WIDTH + x) * 4;
                    const r = data[idx];
                    const g = data[idx + 1];
                    const b = data[idx + 2];
                    colors.push((r << 16) | (g << 8) | b);
                }
            }

            return {
                width: COLOR_SAMPLE_WIDTH,
                height: COLOR_SAMPLE_HEIGHT,
                step: COLOR_SAMPLE_STEP,
                colors,
                roi: validRoi || {
                    x: 0,
                    y: 0,
                    width: videoWidth,
                    height: videoHeight,
                    source: 'full'
                }
            };
        } catch (err) {
            console.warn('Color sampling failed:', err);
            return null;
        }
    }, []);

    const drawFaceDetections = useCallback(async (ctx, videoElement) => {
        const faceapi = faceApiRef.current;
        if (!faceNet || !videoElement || !faceapi) return;

        try {
            const detections = await faceapi.detectAllFaces(
                videoElement,
                new faceapi.TinyFaceDetectorOptions({
                    inputSize: 416,
                    scoreThreshold: 0.4
                })
            ).withFaceLandmarks().withFaceExpressions().withAgeAndGender();

            const validDetections = detections.filter(detection => {
                const box = detection.detection.box;
                return box.width > 30 && box.height > 30;
            });

            const displaySize = { width: videoElement.videoWidth, height: videoElement.videoHeight };

            faceapi.matchDimensions(canvasRef.current, displaySize);

            const resizedDetections = faceapi.resizeResults(validDetections, displaySize);

            faceapi.draw.drawFaceLandmarks(canvasRef.current, resizedDetections);

            resizedDetections.forEach((detection) => {
                const box = detection.detection.box;
                const age = Math.round(detection.age);
                const gender = detection.gender;
                const genderProbability = detection.genderProbability.toFixed(2);
                const expressions = detection.expressions;
                const dominantExpression = Object.keys(expressions).reduce((a, b) =>
                    expressions[a] > expressions[b] ? a : b
                );

                ctx.strokeStyle = '#4285F4';
                ctx.lineWidth = 3;
                ctx.strokeRect(box.x, box.y, box.width, box.height);

                const label = `${age} yrs, ${gender} (${genderProbability}), ${dominantExpression}`;

                ctx.font = '14px Arial';
                const textWidth = ctx.measureText(label).width;
                ctx.fillStyle = 'rgba(66, 133, 244, 0.8)';
                ctx.fillRect(box.x, box.y - 25, textWidth + 10, 20);
                ctx.fillStyle = '#FFFFFF';
                ctx.fillText(label, box.x + 5, box.y - 10);
            });
        } catch (error) {
            console.error('Face drawing error:', error);
        }
    }, [faceNet]);

    const drawHandRadiatingLines = useCallback((ctx, keypoints) => {
        const leftWrist = keypoints[9];
        const rightWrist = keypoints[10];
        const handPoints = [];
        const scoreThreshold = 0.3;

        if (leftWrist && leftWrist.score > scoreThreshold) {
            handPoints.push(leftWrist);
        }
        if (rightWrist && rightWrist.score > scoreThreshold) {
            handPoints.push(rightWrist);
        }

        handPoints.forEach((hand) => {
            const centerX = hand.position.x;
            const centerY = hand.position.y;

            const angles = [0, 45, 90, 135, 180, 225, 270, 315];
            const lineLength = 80;

            angles.forEach(angleDeg => {
                const angle = angleDeg * (Math.PI / 180);
                const endX = centerX + Math.cos(angle) * lineLength;
                const endY = centerY + Math.sin(angle) * lineLength;

                ctx.strokeStyle = '#FF0000';
                ctx.lineWidth = 5;
                ctx.lineCap = 'round';
                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.lineTo(endX, endY);
                ctx.stroke();

                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.lineTo(endX, endY);
                ctx.stroke();

                ctx.fillStyle = '#FF0000';
                ctx.beginPath();
                ctx.arc(endX, endY, 6, 0, 2 * Math.PI);
                ctx.fill();

                ctx.fillStyle = '#FFFFFF';
                ctx.beginPath();
                ctx.arc(endX, endY, 3, 0, 2 * Math.PI);
                ctx.fill();
            });

            ctx.fillStyle = '#FF0000';
            ctx.beginPath();
            ctx.arc(centerX, centerY, 15, 0, 2 * Math.PI);
            ctx.fill();

            ctx.fillStyle = '#FFFFFF';
            ctx.beginPath();
            ctx.arc(centerX, centerY, 8, 0, 2 * Math.PI);
            ctx.fill();
        });
    }, []);

    const drawAIKeypoints = useCallback((ctx, keypoints, color) => {
        keypoints.forEach(keypoint => {
            if (keypoint.score > 0.3) {
                const x = keypoint.position.x;
                const y = keypoint.position.y;

                ctx.beginPath();
                ctx.arc(x, y, 5, 0, 2 * Math.PI);
                ctx.fillStyle = color;
                ctx.fill();

                ctx.beginPath();
                ctx.arc(x, y, 2, 0, 2 * Math.PI);
                ctx.fillStyle = '#FFFFFF';
                ctx.fill();
            }
        });
    }, []);

    const drawAISkeleton = useCallback((ctx, keypoints, color) => {
        const connections = [
            [5, 6], [5, 7], [6, 8], [7, 9], [8, 10],
            [5, 11], [6, 12], [11, 12], [11, 13], [12, 14],
            [13, 15], [14, 16], [0, 1], [0, 2], [1, 3], [2, 4]
        ];

        connections.forEach(([from, to]) => {
            const fromPoint = keypoints[from];
            const toPoint = keypoints[to];

            if (fromPoint && toPoint && fromPoint.score > 0.3 && toPoint.score > 0.3) {
                const fromX = fromPoint.position.x;
                const fromY = fromPoint.position.y;
                const toX = toPoint.position.x;
                const toY = toPoint.position.y;

                ctx.lineWidth = 3;
                ctx.strokeStyle = color;
                ctx.lineCap = 'round';
                ctx.beginPath();
                ctx.moveTo(fromX, fromY);
                ctx.lineTo(toX, toY);
                ctx.stroke();
            }
        });
    }, []);

    const drawPersonBoundingBox = useCallback((ctx, keypoints, color, personIndex) => {
        const validPoints = keypoints.filter(kp => kp.score > 0.3);
        if (validPoints.length === 0) return;

        const xs = validPoints.map(kp => kp.position.x);
        const ys = validPoints.map(kp => kp.position.y);

        const minX = Math.min(...xs) - 20;
        const maxX = Math.max(...xs) + 20;
        const minY = Math.min(...ys) - 20;
        const maxY = Math.max(...ys) + 20;

        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 4]);
        ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
        ctx.setLineDash([]);

        ctx.fillStyle = color;
        ctx.font = 'bold 14px Arial';
        ctx.fillText(`Person ${personIndex + 1}`, minX, minY - 5);
    }, []);

    const drawAISkeletonVisualization = useCallback(async (poses, pointingData, expressionBoxes = []) => {
        if (!canvasRef.current || !videoRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const video = videoRef.current;
        if (!canvas) return;

        if (video.videoWidth === 0 || video.videoHeight === 0) return;

        if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const colors = ['#00FF00', '#0080FF', '#FF4000', '#FFFF00', '#FF00FF'];

        poses.forEach((pose, personIndex) => {
            if (pose.score > 0.3) {
                const color = colors[personIndex % colors.length];
                drawAIKeypoints(ctx, pose.keypoints, color);
                drawAISkeleton(ctx, pose.keypoints, color);
                drawPersonBoundingBox(ctx, pose.keypoints, color, personIndex);
            }
        });

        // Draw only the selected face part marker (avoid full mesh clutter)
        const currentFaceSelection = faceSelectionRef.current;
        const selectedPart = currentFaceSelection ? normalizeFacePartKey(currentFaceSelection.part || 'nose') : null;
        const partData = selectedPart ? window.humanDetectionData?.faceParts?.[selectedPart] : null;
        if (currentFaceSelection && partData && partData.x !== undefined && partData.y !== undefined) {
            ctx.fillStyle = '#00FF88';
            ctx.strokeStyle = '#003322';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(partData.x, partData.y, 10, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = '#FFFFFF';
            ctx.font = 'bold 14px Arial';
            ctx.fillText(formatLabel(selectedPart), partData.x + 12, partData.y - 12);
        }

        // Draw selected finger joint marker if available
        const currentHandSelection = handSelectionRef.current;
        const fingerSelection = currentHandSelection ? normalizeFinger(currentHandSelection.finger || DEFAULT_FINGER) : null;
        const handLandmarks = currentHandSelection ? window.humanDetectionData?.handLandmarks : null;
        const landmarkIndex = fingerSelection ? getHandLandmarkIndex(fingerSelection) : null;
        const jointPoint = landmarkIndex !== null && handLandmarks ? handLandmarks?.[landmarkIndex] : null;
        if (
            currentHandSelection &&
            jointPoint &&
            typeof jointPoint.x === 'number' &&
            typeof jointPoint.y === 'number' &&
            video.videoWidth > 0 &&
            video.videoHeight > 0
        ) {
            const jointX = jointPoint.x * video.videoWidth;
            const jointY = jointPoint.y * video.videoHeight;
            ctx.fillStyle = '#FF6AD5';
            ctx.strokeStyle = '#5B1A4D';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(jointX, jointY, 10, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = '#FFFFFF';
            ctx.font = 'bold 14px Arial';
            const fingerLabel = formatLabel(fingerSelection);
            ctx.fillText(`${fingerLabel} tip`.trim(), jointX + 12, jointY - 12);
        }

        if (Array.isArray(expressionBoxes) && expressionBoxes.length > 0) {
            ctx.font = 'bold 16px Arial';
            ctx.textBaseline = 'top';
            expressionBoxes.forEach((item, index) => {
                if (!item || !item.box) return;
                const { box, expression, confidence } = item;
                const labelExpression = (expression || 'unknown').toUpperCase();
                const confidencePercent = Number.isFinite(confidence) ? Math.round(confidence * 100) : 0;
                const label = `${labelExpression} ${confidencePercent}%`;

                ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
                ctx.lineWidth = 3;
                ctx.setLineDash([10, 6]);
                ctx.strokeRect(box.x, box.y, box.width, box.height);
                ctx.setLineDash([]);

                const padding = 8;
                const textWidth = ctx.measureText(label).width;
                const textX = box.x;
                const textY = Math.max(0, box.y - 26);

                ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
                ctx.fillRect(textX - 2, textY - 4, textWidth + padding, 24);
                ctx.fillStyle = '#00FFAF';
                ctx.fillText(label, textX + 2, textY - 2);
            });
        }
        if (pointingData) {
            drawPointingIndicator(ctx, pointingData);
        }
    }, [drawPointingIndicator, faceNet, drawAIKeypoints, drawAISkeleton, drawPersonBoundingBox]);

    const startDetection = useCallback(() => {
        if (!net || !cameraStarted || detectionActive.current) {
            console.log('⚠️ Detection not ready');
            setDetectionStatus('Detection not ready');
            return;
        }

        const faceapi = faceApiRef.current;
        const handUtils = handModuleRef.current;

        detectionActive.current = true;
        setDetectionStatus('Detection running');
        console.log('🚀 Starting AI detection');

        const detect = async () => {
            if (!detectionActive.current || !net || !videoRef.current ||
                !streamRef.current || videoRef.current.readyState !== 4) {
                return;
            }

            if (document.hidden) {
                detectionActive.current = false;
                setDetectionStatus('Paused (tab hidden)');
                return;
            }

            const now = performance.now();
            if (now - lastDetectionTimeRef.current < FRAME_INTERVAL_MS) {
                animationIdRef.current = requestAnimationFrame(detect);
                return;
            }
            lastDetectionTimeRef.current = now;

            try {
                const poses = await net.estimateMultiplePoses(videoRef.current, {
                    flipHorizontal: false,
                    maxDetections: 3,
                    scoreThreshold: 0.1,
                    nmsRadius: 30
                });

                let detectedPeople = 0;
                let handsFound = 0;
                let facesFound = 0;

                const validPeopleCount = poses.filter(pose => {
                    const visibleKeypoints = pose.keypoints.filter(kp => kp.score > 0.2).length;
                    return pose.score > 0.2 && visibleKeypoints >= 5;
                }).length;
                detectedPeople = Math.min(validPeopleCount, 1);

                if (poses.length > 0) {
                    handsFound = checkHandDetected(poses);
                }

                if (faceNet) {
                    facesFound = await checkFaceDetected(videoRef.current);
                }

                let faceParts = null;
                if (faceMeshReadyRef.current && faceLandmarkerRef.current) {
                    try {
                        const result = faceLandmarkerRef.current.detectForVideo(videoRef.current, performance.now());
                        const landmarks = result?.faceLandmarks?.[0];
                        if (landmarks && landmarks.length) {
                            const w = videoRef.current.videoWidth || videoRef.current.videoWidth;
                            const h = videoRef.current.videoHeight || videoRef.current.videoHeight;
                            const toPx = (idx) => {
                                const p = landmarks[idx];
                                if (!p) return null;
                                return { x: p.x * w, y: p.y * h, z: p.z, score: p.visibility ?? 1 };
                            };
                            const avg = (indices) => {
                                const pts = indices.map(toPx).filter(Boolean);
                                if (!pts.length) return null;
                                const sum = pts.reduce((acc, p) => ({
                                    x: acc.x + p.x,
                                    y: acc.y + p.y,
                                    z: acc.z + (p.z || 0),
                                    score: acc.score + (p.score || 1)
                                }), { x: 0, y: 0, z: 0, score: 0 });
                                const n = pts.length || 1;
                                return { x: sum.x / n, y: sum.y / n, z: sum.z / n, score: sum.score / n };
                            };

                            const leftEye = avg([33, 133, 159, 145]);
                            const rightEye = avg([263, 362, 386, 374]);

                            faceParts = {
                                nose: toPx(1),
                                mouth: avg([13, 14]),
                                leftEye,
                                rightEye,
                                betweenEyes: (leftEye && rightEye)
                                    ? { x: (leftEye.x + rightEye.x) / 2, y: (leftEye.y + rightEye.y) / 2, score: (leftEye.score + rightEye.score) / 2 }
                                    : null,
                                leftEar: toPx(234),
                                rightEar: toPx(454),
                                topOfHead: toPx(10),
                            };

                            facesFound = Math.max(facesFound, result.faceLandmarks.length);
                        }
                    } catch (err) {
                        console.error('Face Landmarker error:', err);
                    }
                }

                if (handUtils?.getFingerDirection && detectorRef.current) {
                    try {
                        const handResults = detectorRef.current.detectForVideo(videoRef.current, performance.now());
                        const hands = handResults?.landmarks?.[0];

                        if (hands) {
                            const direction = handUtils.getFingerDirection(hands);
                            const palmState = typeof handUtils.getPalmOpenState === 'function'
                                ? handUtils.getPalmOpenState(hands)
                                : null;
                            console.log('dYY? Hand detected by MediaPipe:', direction);

                            window.handDetectionData = {
                                direction: direction,
                                landmarks: hands,
                                detected: true,
                                handOpen: palmState ? palmState.isOpen : null,
                                handOpenCount: palmState ? palmState.extendedCount : null
                            };
                        } else {
                            window.handDetectionData = {
                                direction: null,
                                landmarks: null,
                                detected: false,
                                handOpen: null,
                                handOpenCount: null
                            };
                        }
                    } catch (err) {
                        console.error('Hand detection error:', err);
                        window.handDetectionData = { direction: null, landmarks: null, detected: false, handOpen: null, handOpenCount: null };
                    }
                }

                let dominantExpression = null;
                let leftHand = null;
                let rightHand = null;
                let expressionBoxes = [];

                if (facesFound > 0 && faceapi) {
                    try {
                        const detections = await faceapi.detectAllFaces(
                            videoRef.current,
                            new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.5 })
                        ).withFaceExpressions();

                        expressionBoxes = detections.map(det => {
                            const expressions = det.expressions || {};
                            const expressionKeys = Object.keys(expressions);
                            const primaryExpression = expressionKeys.reduce(
                                (prev, curr) => (expressions[prev] > expressions[curr] ? prev : curr),
                                expressionKeys[0] || 'neutral'
                            );
                            return {
                                box: det.detection.box,
                                expression: primaryExpression,
                                confidence: expressions[primaryExpression] || 0
                            };
                        });

                        if (expressionBoxes.length > 0) {
                            dominantExpression = expressionBoxes[0].expression;
                        }
                    } catch (error) {
                        console.error('Expression detection error:', error);
                    }
                }

                if (poses.length > 0) {
                    const firstPose = poses[0];
                    leftHand = firstPose.keypoints[9];
                    rightHand = firstPose.keypoints[10];
                }

                setPeopleCount(detectedPeople);
                setHandCount(handsFound);
                setFaceCount(facesFound);

                const validPoses = poses.filter(pose => pose.score > 0.2);
                const emotionHudPayload = emotionHudEnabledRef.current ? expressionBoxes : [];
                await drawAISkeletonVisualization(validPoses.slice(0, 1), null, emotionHudPayload);

                const colorRoi = buildColorSamplingRoi({
                    faceBoxes: expressionBoxes.map(entry => entry.box),
                    leftHand,
                    rightHand,
                    videoWidth: videoRef.current?.videoWidth || 0,
                    videoHeight: videoRef.current?.videoHeight || 0
                });
                const colorSamples = getColorSamples(colorRoi);

                window.humanDetectionData = {
                    handCount: handsFound,
                    peopleCount: detectedPeople,
                    faceCount: facesFound,
                    faceParts: faceParts,
                    poses: validPoses.slice(0, 1),
                    timestamp: Date.now(),
                    cameraActive: cameraStarted,
                    dominantExpression: dominantExpression,
                    leftHand: leftHand,
                    rightHand: rightHand,
                    handDetected: window.handDetectionData?.detected || false,
                    handDirection: window.handDetectionData?.direction || null,
                    handLandmarks: window.handDetectionData?.landmarks || null,
                    handOpen: window.handDetectionData?.handOpen ?? null,
                    handOpenCount: window.handDetectionData?.handOpenCount ?? null,
                    videoWidth: videoRef.current?.videoWidth || 0,
                    videoHeight: videoRef.current?.videoHeight || 0,
                    videoEl: videoRef.current || null,
                    videoOpacity: window.humanDetectionData?.videoOpacity !== undefined ? window.humanDetectionData.videoOpacity : 100,
                    colorSamples: colorSamples,
                    colorDetection: window.humanDetectionData?.colorDetection || null
                };

                if (detectedPeople > 0 || handsFound > 0 || facesFound > 0) {
                    console.log(`🤖 Independent Detection: ${detectedPeople} people, ${handsFound} hands, ${facesFound} faces`);
                }

            } catch (error) {
                console.error('AI Detection error:', error);
                setDetectionStatus(`Detection error: ${error.message}`);
            }

            if (detectionActive.current) {
                animationIdRef.current = requestAnimationFrame(detect);
            }
        };

        detect();
    }, [net, cameraStarted, checkHandDetected, checkFaceDetected, faceNet, drawAISkeletonVisualization]);

    useEffect(() => {
        if (cameraStarted && net) {
            startDetection();
        }
    }, [cameraStarted, net, startDetection]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                detectionActive.current = false;
                if (animationIdRef.current) {
                    cancelAnimationFrame(animationIdRef.current);
                    animationIdRef.current = null;
                }
                setDetectionStatus('Paused (tab hidden)');
            } else if (cameraStarted && net) {
                startDetection();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [cameraStarted, net, startDetection]);

    useEffect(() => {
        window.humanDetectionController = {
            startCamera,
            stopCamera,
            isActive: () => cameraStarted,
            getData: () => window.humanDetectionData || {}
        };
        return () => { delete window.humanDetectionController; };
    }, [startCamera, stopCamera, cameraStarted]);

    useEffect(() => {
        if (videoRef.current?.parentElement) {
            videoRef.current.parentElement.style.opacity = videoOpacity / 100;
        }
    }, [videoOpacity]);

    useEffect(() => {
        return () => {
            detectionActive.current = false;
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            if (animationIdRef.current) {
                cancelAnimationFrame(animationIdRef.current);
            }
        };
    }, []);

    const aiLoadingBanner = showAiLoadingBanner ? (
        <div className="ai-loading-banner" role="status" aria-live="polite">
            <div className="ai-loading-banner__message">
                Hey champ, please wait while we load the AI libraries for you!
            </div>
            {/* {loadingStatus && (
 <div className="ai-loading-banner__status">{loadingStatus}</div>
 )} */}
        </div>
    ) : null;

    if (!isVisible) {
        return aiLoadingBanner || null;
    }

    const colorStatus = window.humanDetectionData?.colorDetection;
    const showColorStatus = colorStatus && colorStatus.targetColor;
    const colorStatusLabel = showColorStatus
        ? `${colorStatus.matched ? "Color detected" : "Searching"} ${colorStatus.targetColor}`
        : "";

    return (
        <>
            {aiLoadingBanner}
            <div
                style={{
                    position: 'fixed',
                    top: `${stageRect.top}px`,
                    left: `${stageRect.left}px`,
                    width: `${stageRect.width}px`,
                    height: `${stageRect.height}px`,
                    backgroundColor: '#000',
                    zIndex: 10000,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: 'none',
                    opacity: videoOpacity / 100,
                }}
            >
                <video
                    ref={videoRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                    autoPlay
                    muted
                    playsInline
                />
                <canvas
                    ref={canvasRef}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none'
                    }}
                />
                {numberDetectEnabled && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: `${NUMBER_DETECT_CROP.widthRatio * 100}%`,
                            height: `${NUMBER_DETECT_CROP.heightRatio * 100}%`,
                            transform: 'translate(-50%, -50%)',
                            border: '2px dashed rgba(255, 255, 255, 0.9)',
                            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
                            borderRadius: '12px',
                            pointerEvents: 'none',
                            zIndex: 10001,
                        }}
                    />
                )}
                {showColorStatus && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.65)',
                            color: '#FFFFFF',
                            padding: '6px 10px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            zIndex: 10003
                        }}
                    >
                        <span
                            style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: colorStatus.targetColor,
                                border: '1px solid #FFFFFF',
                                display: 'inline-block'
                            }}
                        />
                        <span>{colorStatusLabel}</span>
                    </div>
                )}
                <div style={{
                    position: 'absolute',
                    bottom: '0px',
                    left: '0px',
                    width: '100%',
                    textAlign: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    color: '#00FF00',
                    padding: '12px 0',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    borderRadius: '0 0 12px 12px',
                    borderTop: '2px solid #00FF00',
                    fontFamily: 'monospace',
                    textShadow: '0 0 10px #00FF00',
                    zIndex: 10002,
                }}>
                    <span>🤖 HUMANS: {peopleCount} | ✋ HANDS: {handCount} | 😊 FACES: {faceCount}</span>
                </div>
            </div>
        </>
    );
};

export default HumanDetectionFullStage;
